/**
 * Utilities for checking validation and TARIC JSON errors
 */

/**
 * Check if validation errors exist
 */
export const hasValidationErrors = (validationErrors) => {
  if (!validationErrors) return false;
  
  if (Array.isArray(validationErrors)) {
    return validationErrors.length > 0 && validationErrors.some(err => {
      if (!err) return false;
      return (
        (err.declarationErrors && Array.isArray(err.declarationErrors) && err.declarationErrors.length > 0) ||
        (err.itemErrors && Array.isArray(err.itemErrors) && err.itemErrors.length > 0) ||
        (err.documentErrors && Array.isArray(err.documentErrors) && err.documentErrors.length > 0) ||
        (err.packageErrors && Array.isArray(err.packageErrors) && err.packageErrors.length > 0)
      );
    });
  }
  
  if (typeof validationErrors === 'object') {
    return Object.keys(validationErrors).length > 0;
  }
  
  return false;
};

/**
 * Fast check if errors object/array has actual errors (lightweight version)
 * Used for priority checks before comprehensive validation
 */
export const hasActualTaricErrors = (errors) => {
  if (!errors) return false;
  if (Array.isArray(errors)) return errors.length > 0;
  if (errors.errors && Array.isArray(errors.errors)) return errors.errors.length > 0;
  return Object.keys(errors).length > 0;
};

/**
 * Get TARIC JSON errors with priority: workflow > tab > global
 * Only returns errors if they actually exist (prevents false positives)
 * @param {Object} options - Configuration object
 * @param {Object} options.workflowErrors - Errors from workflow session state
 * @param {Object} options.tabErrors - Errors from active tab
 * @param {Object} options.globalErrors - Errors from global state
 * @param {boolean} options.hasTabs - Whether tabs exist
 * @returns {Object|null} - Errors object or null if no actual errors
 */
export const getTaricJsonErrorsWithPriority = ({ workflowErrors, tabErrors, globalErrors, hasTabs }) => {
  // Priority 1: Active workflow session's taricJsonErrors
  if (workflowErrors !== undefined && workflowErrors !== null) {
    return hasActualTaricErrors(workflowErrors) ? workflowErrors : null;
  }
  
  // Priority 2: Active tab errors (only if has actual errors)
  if (hasTabs && tabErrors !== null && tabErrors !== undefined) {
    return hasActualTaricErrors(tabErrors) ? tabErrors : null;
  }
  
  // Priority 3: Global state (only if has actual errors)
  return hasActualTaricErrors(globalErrors) ? globalErrors : null;
};

/**
 * Check if TARIC JSON errors exist
 */
export const hasTaricJsonErrors = (taricJsonErrors) => {
  if (!taricJsonErrors) return false;
  
  if (typeof taricJsonErrors === 'object' && !Array.isArray(taricJsonErrors)) {
    // Check for new error format with errors array
    if (Array.isArray(taricJsonErrors.errors)) {
      // CRITICAL: Only return true if errors array has items AND they contain actual errors
      if (taricJsonErrors.errors.length > 0) {
        // Check if there are any actual error objects (not just empty arrays)
        const hasActualErrors = taricJsonErrors.errors.some(errorObj => {
          if (!errorObj || typeof errorObj !== 'object') return false;
          
          // Check for clientes errors
          if (errorObj.clientes && typeof errorObj.clientes === 'object' && Object.keys(errorObj.clientes).length > 0) {
            return true;
          }
          
          // Check for exportaciones errors
          if (errorObj.exportaciones && typeof errorObj.exportaciones === 'object') {
            // Check direct export-level errors
            const exportKeys = Object.keys(errorObj.exportaciones).filter(k => k !== 'partidas');
            if (exportKeys.length > 0) return true;
            
            // Check partidas errors
            if (errorObj.exportaciones.partidas && Array.isArray(errorObj.exportaciones.partidas)) {
              return errorObj.exportaciones.partidas.some(partida => {
                if (!partida || typeof partida !== 'object') return false;
                
                // Check direct partida errors
                const partidaKeys = Object.keys(partida).filter(k => k !== 'documentos' && k !== 'embalajes');
                if (partidaKeys.length > 0) return true;
                
                // Check documentos errors
                if (partida.documentos && Array.isArray(partida.documentos)) {
                  const hasDocErrors = partida.documentos.some(doc => {
                    return doc !== null && typeof doc === 'object' && Object.keys(doc).length > 0;
                  });
                  if (hasDocErrors) return true;
                }
                
                // Check embalajes errors
                if (partida.embalajes && Array.isArray(partida.embalajes)) {
                  const hasEmbErrors = partida.embalajes.some(emb => {
                    if (emb === null || typeof emb !== 'object') return false;
                    // Check if any property is a string (error message)
                    return Object.values(emb).some(val => typeof val === 'string' && val.length > 0);
                  });
                  if (hasEmbErrors) return true;
                }
                
                return false;
              });
            }
          }
          
          return false;
        });
        return hasActualErrors;
      }
      // If errors array is empty, no errors
      return false;
    }
    // For old format, check if object has error properties (exclude success array)
    const keys = Object.keys(taricJsonErrors).filter(k => k !== 'success');
    return keys.length > 0;
  }
  
  if (Array.isArray(taricJsonErrors)) {
    return taricJsonErrors.length > 0;
  }
  
  return false;
};

/**
 * Whether a specific export index (0-based) has any validation errors.
 */
export const hasValidationErrorsForExport = (validationErrors, exportIndex) => {
  if (!validationErrors || !Array.isArray(validationErrors)) return false;
  const decl = validationErrors[exportIndex];
  return hasValidationErrors(decl ? [decl] : []);
};

/**
 * Get field label from validation errors
 */
export const getFieldLabelFromValidationErrors = (fieldId, validationErrors) => {
  if (!fieldId || !validationErrors || !Array.isArray(validationErrors)) {
    return null;
  }
  
  // Generate search field IDs (try multiple formats)
  const searchFieldIds = [fieldId];
  
  // Handle document fields
  const docMatch = fieldId.match(/^documents_(.+)_(\d+)_(\d+)_(\d+)$/);
  if (docMatch) {
    const [, fieldName, declIdx, itemIdx, docIdx] = docMatch;
    searchFieldIds.push(`documents_${fieldName}_${parseInt(declIdx) - 1}_${itemIdx}_${docIdx}`);
  }
  
  // Handle package fields
  const pkgMatch = fieldId.match(/^packages_(.+)_(\d+)_(\d+)_(\d+)$/);
  if (pkgMatch && !fieldId.startsWith('packages_marcas_marca')) {
    const [, fieldName, declIdx, itemIdx, embajaIdx] = pkgMatch;
    searchFieldIds.push(`packages_${fieldName}_${parseInt(declIdx) - 1}_${itemIdx}_${embajaIdx}`);
  }
  
  // Handle package marca fields
  const marcaMatch = fieldId.match(/^packages_marcas_marca_(\d+)_(\d+)_(\d+)_(\d+)$/);
  if (marcaMatch) {
    const [, declIdx, itemIdx, embajaIdx, marcaIdx] = marcaMatch;
    searchFieldIds.push(`packages_marcas_marca_${parseInt(declIdx) - 1}_${itemIdx}_${embajaIdx}_${marcaIdx}`);
  }
  
  // Handle item fields
  const itemMatch = fieldId.match(/^(.+)_(\d+)_(\d+)$/);
  if (itemMatch && !fieldId.startsWith('documents_') && !fieldId.startsWith('packages_')) {
    const [, fieldName, declIdx, itemIdx] = itemMatch;
    searchFieldIds.push(`${fieldName}_${parseInt(declIdx) - 1}_${itemIdx}`);
  }
  
  // Handle declaration fields
  const declMatch = fieldId.match(/^(.+)_(\d+)$/);
  if (declMatch && !fieldId.startsWith('documents_') && !fieldId.startsWith('packages_') && !itemMatch) {
    const [, fieldName, declIdx] = declMatch;
    searchFieldIds.push(`${fieldName}_${parseInt(declIdx) - 1}`);
  }
  
  // Search through validationErrors
  for (const declaration of validationErrors) {
    const searchInErrors = (errors) => {
      if (!Array.isArray(errors)) return null;
      for (const searchId of searchFieldIds) {
        const found = errors.find(err => err.field === searchId);
        if (found) return found.error;
      }
      return null;
    };
    
    if (declaration.declarationErrors) {
      const result = searchInErrors(declaration.declarationErrors);
      if (result) return result;
    }
    
    if (declaration.itemErrors) {
      for (const item of declaration.itemErrors) {
        if (item.errors) {
          const result = searchInErrors(item.errors);
          if (result) return result;
        }
      }
    }
    
    if (declaration.documentErrors) {
      for (const doc of declaration.documentErrors) {
        if (doc.errors) {
          const result = searchInErrors(doc.errors);
          if (result) return result;
        }
      }
    }
    
    if (declaration.packageErrors) {
      for (const pkg of declaration.packageErrors) {
        if (pkg.errors) {
          const result = searchInErrors(pkg.errors);
          if (result) return result;
        }
      }
    }
  }
  
  return null;
};

